import React from 'react';
import Link from 'next/link';
import { twMerge } from 'tailwind-merge';
import { isPathExternalLink } from '@/ui/utils/localization-helpers/localization';
import { isInternalPath, isSafeHref } from '@/utils/url-helper';

const NEW_TAB_TARGET = '_blank';

interface RichTextLinkProps {
  href?: string | null;
  rel?: string | null;
  /** Value of the "Target" field in the Crystallize link dialog, e.g. "_blank". */
  target?: string | null;
  className?: string;
  openExternalLinksInNewTab?: boolean;
  children: React.ReactNode;
}

/**
 * Renders a single rich-text link. Kept out of the RichTextRenderer switch so the renderer stays
 * below the cognitive complexity limit. Pure and hook-free, because server components render it.
 *
 * Tab behaviour, in order of precedence:
 * 1. A "Target" the editor set in the CMS link dialog wins: "_blank" opens a new tab, any other
 *    value keeps the current tab. Named targets are not passed through on purpose.
 * 2. Without an editor target, `openExternalLinksInNewTab` opens links to other domains in a
 *    new tab.
 * 3. Otherwise the link navigates in the current tab.
 *
 * Paths on this site use Next.js Link, so they navigate without reloading the page.
 */
export function RichTextLink({
  href,
  rel,
  target,
  className,
  openExternalLinksInNewTab = false,
  children,
}: Readonly<RichTextLinkProps>) {
  // Editors can save any scheme in the CMS, so drop the anchor for unsafe ones
  // (javascript:, data:, ...) and keep the text readable.
  if (!href || !isSafeHref(href)) {
    return <span className={className}>{children}</span>;
  }

  const editorTarget = target?.trim().toLowerCase();
  const shouldOpenInNewTab = editorTarget
    ? editorTarget === NEW_TAB_TARGET
    : openExternalLinksInNewTab && isPathExternalLink(href);

  const anchorProps = {
    className: twMerge('underline duration-200 ease-in hover:text-primary-100', className),
    href,
    target: shouldOpenInNewTab ? NEW_TAB_TARGET : undefined,
    rel: shouldOpenInNewTab ? 'noopener noreferrer' : (rel ?? undefined),
  };

  if (isInternalPath(href)) {
    return <Link {...anchorProps}>{children}</Link>;
  }

  return <a {...anchorProps}>{children}</a>;
}
